import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import { Box, Button, Collapse, IconButton, Stack, Typography } from "@mui/material";
import {
  amber,
  blue,
  blueGrey,
  brown,
  deepOrange,
  green,
  grey,
  indigo,
  purple,
  teal,
} from "@mui/material/colors";
import {
  BarPlot,
  BarSeriesType,
  ChartContainerPro,
  ChartsAxisHighlight,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
} from "@mui/x-charts-pro";
import { BigTooltip } from "components/BigTooltip";
import { WasteGenerationChartsQuery } from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatNumber } from "util/formatNumber";

import { formatMonth } from "../WasteShipments/utils";
import { MonthDetailsView } from "./MonthDetailsView";

type GenerationRow =
  WasteGenerationChartsQuery["facilityWasteGenerationCharts"]["generationByMonth"][number];

type Props = {
  data: GenerationRow[];
};

/**
 * Stable color assignment for the eight known WADM classification labels plus
 * the demo sentinel. Any unknown classification gets a deterministic fallback
 * color from {@link FALLBACK_COLORS} so the legend stays predictable for QA
 * even if Amazon ships a new label.
 */
const CLASSIFICATION_COLORS: Record<string, string> = {
  "Hazardous Waste": deepOrange[300],
  "Universal Waste": amber[500],
  "E-Waste": teal[400],
  "Non Hazardous Waste": green[400],
  Recyclables: indigo[300],
  "Municipal/General Waste": brown[300],
  Pharmacy: purple[300],
  "Medical/Biological/Clinical Waste": blue[400],
  "State-Regulated (Demo)": blueGrey[400],
  Uncategorized: grey[500],
};

const FALLBACK_COLORS = [grey[600], grey[700], grey[800]];

function colorFor(classification: string, fallbackIndex: number): string {
  return (
    CLASSIFICATION_COLORS[classification] ?? FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length]
  );
}

export function WasteGenerationByClassificationGraph({ data }: Props) {
  // Collect every classification that appears at least once in the date range,
  // ordered by total lbs descending so the largest sit at the bottom of the
  // stack (most visually grounded).
  const classifications = useMemo(() => {
    const totals = new Map<string, number>();
    for (const month of data) {
      for (const entry of month.byClassification ?? []) {
        totals.set(entry.classification, (totals.get(entry.classification) ?? 0) + entry.lbs);
      }
    }
    return [...totals.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([classification]) => classification);
  }, [data]);

  const dataset = useMemo(
    () =>
      data.map((row, index) => {
        const byClassificationMap = new Map<string, number>();
        for (const entry of row.byClassification ?? []) {
          byClassificationMap.set(entry.classification, entry.lbs);
        }
        const rawMonth = monthDateToString(row.month);
        const monthLabel = formatMonth(rawMonth, index === 0);
        const cells: Record<string, number | string> = { month: monthLabel, rawMonth };
        for (const classification of classifications) {
          cells[classification] = byClassificationMap.get(classification) ?? 0;
        }
        return cells;
      }),
    [data, classifications]
  );

  const series: BarSeriesType[] = useMemo(
    () =>
      classifications.map((classification, index) => ({
        type: "bar",
        dataKey: classification,
        stack: "classification",
        color: colorFor(classification, index),
        label: classification,
        valueFormatter: (value) => `${formatNumber(value ?? 0)} lbs`,
      })),
    [classifications]
  );

  const maxValue = useMemo(() => {
    const max = data.reduce((acc, m) => {
      const total = (m.byClassification ?? []).reduce((s, e) => s + e.lbs, 0);
      return Math.max(acc, total);
    }, 0);
    if (max <= 0) return 100;
    const sqrt = Math.sqrt(max) * 1.1;
    const padded = sqrt * sqrt;
    const tickStep = 500;
    return Math.ceil(padded / tickStep) * tickStep;
  }, [data]);

  // Manual chart hover tracking — drives the column highlight (`highlightedAxis`)
  // and enables clicking anywhere in a month column (even one with empty/short
  // bars) to open the Detail View on that month. Mirrors the Waste Shipments
  // tile + the Generator-status view of this card.
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [hoveredMonthIndex, setHoveredMonthIndex] = useState<number | null>(null);
  const monthIndexFromEvent = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): number | null => {
      const node = chartContainerRef.current;
      if (!node || dataset.length === 0) return null;
      const rect = node.getBoundingClientRect();
      const leftAxisOffset = 80;
      const chartWidth = rect.width - leftAxisOffset;
      if (chartWidth <= 0) return null;
      const x = event.clientX - rect.left - leftAxisOffset;
      if (x < 0) return null;
      const barWidth = chartWidth / dataset.length;
      const index = Math.floor(x / barWidth);
      return index >= 0 && index < dataset.length ? index : null;
    },
    [dataset]
  );
  const handleChartMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const index = monthIndexFromEvent(event);
      setHoveredMonthIndex((prev) => (prev === index ? prev : index));
    },
    [monthIndexFromEvent]
  );
  const handleChartMouseLeave = useCallback(() => {
    setHoveredMonthIndex((prev) => (prev === null ? prev : null));
  }, []);
  const highlightedAxis = useMemo(
    () => (hoveredMonthIndex !== null ? [{ axisId: "x-axis", dataIndex: hoveredMonthIndex }] : []),
    [hoveredMonthIndex]
  );

  // Detail View state — default to the first month with any WADM rows so the
  // picker opens to something meaningful. Independent of the Generator-status
  // view's selection (the two views show different framings; resetting per-view
  // is fine).
  const [monthDetailsCollapsed, setMonthDetailsCollapsed] = useState(true);
  const defaultSelectedMonth = useMemo(() => {
    const monthWithClassifications = data.find((row) => (row.byClassification ?? []).length > 0);
    const pick = monthWithClassifications ?? data[0] ?? null;
    return pick ? monthDateToString(pick.month) : null;
  }, [data]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(defaultSelectedMonth);
  const availableMonths = useMemo(() => data.map((row) => monthDateToString(row.month)), [data]);
  useEffect(() => {
    if (selectedMonth && !availableMonths.includes(selectedMonth) && defaultSelectedMonth) {
      setSelectedMonth(defaultSelectedMonth);
    }
  }, [selectedMonth, availableMonths, defaultSelectedMonth]);
  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const index = monthIndexFromEvent(event);
      if (index == null) return;
      setSelectedMonth(monthDateToString(data[index].month));
      setMonthDetailsCollapsed(false);
    },
    [monthIndexFromEvent, data]
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1">Waste generated by classification in lbs</Typography>
          <BigTooltip
            title={
              <Typography variant="caption">
                Full waste profile from Amazon's Waste and Donation Management (WADM) feed, broken
                down by waste classification. Includes classifications the federal generator-status
                view excludes (Universal Waste, E-Waste, Pharmacy, etc.) so you can see the entire
                generation picture.
              </Typography>
            }
          >
            <IconButton size="small">
              <InfoOutlined color="disabled" />
            </IconButton>
          </BigTooltip>
        </Stack>
      </Stack>

      <Box
        ref={chartContainerRef}
        onMouseMove={handleChartMouseMove}
        onMouseLeave={handleChartMouseLeave}
        onClick={handleChartClick}
        sx={{ height: 300, width: "100%", cursor: "pointer" }}
      >
        <ChartContainerPro
          dataset={dataset}
          series={series}
          sx={{
            "& .MuiChartsAxisHighlight-root": {
              fill: (theme) => theme.palette.grey[500],
              fillOpacity: 0.2,
            },
          }}
          highlightedAxis={highlightedAxis}
          xAxis={[
            {
              id: "x-axis",
              scaleType: "band",
              dataKey: "month",
              categoryGapRatio: 0.3,
              height: 40,
            },
          ]}
          yAxis={[
            {
              id: "y-axis",
              scaleType: "sqrt",
              min: 0,
              max: maxValue,
              valueFormatter: (value) => formatNumber(value),
              width: 60,
            },
          ]}
          margin={{ left: 20, right: 20, top: 16, bottom: 0 }}
          height={300}
        >
          <BarPlot
            slotProps={{ bar: { rx: 3, ry: 3, style: { strokeWidth: 1, stroke: "white" } } }}
          />
          <ChartsAxisHighlight x="band" />
          <ChartsXAxis />
          <ChartsYAxis />
          <ChartsTooltip trigger="axis" />
        </ChartContainerPro>
      </Box>

      <Box mt={2}>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          {classifications.map((classification, index) => (
            <Stack key={classification} direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  backgroundColor: colorFor(classification, index),
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {classification}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {data.length > 0 && (
        <Stack spacing={2} mt={1}>
          <Box>
            <Button
              onClick={() => setMonthDetailsCollapsed((prev) => !prev)}
              endIcon={monthDetailsCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            >
              {monthDetailsCollapsed ? "View Month Details" : "Collapse Month Details"}
            </Button>
          </Box>
          <Collapse in={!monthDetailsCollapsed} unmountOnExit>
            <MonthDetailsView
              selectedMonth={selectedMonth}
              onMonthChange={setSelectedMonth}
              data={data}
            />
          </Collapse>
        </Stack>
      )}
    </>
  );
}

function monthDateToString(value: Date | string): string {
  if (typeof value === "string") return value.slice(0, 10);
  return value.toISOString().slice(0, 10);
}
